import React, {useEffect, useState} from "react";
import {AxiosResponse} from "axios";
import {DataTable} from "../../../Component/Common/Components/DataTable/DataTable";
import {EntriesPerPage} from "../../../Component/Model/Table/Table";
import {SearchResult} from "../../../Component/Model/Dto/SearchResult";
import {StagedData} from "../../../Component/Model/Dto/Staging/StagedData";

interface IProps<TRow> {
    requestId: number;
    failingOnly: boolean;
    search: (requestId: number, request: StagedData.SearchRequest) => Promise<AxiosResponse<SearchResult<TRow>>>;
    mapper: (rows: TRow[]) => { columns: any[], rows: any[] };
    noDataMessage: string;
}

// One tab of the staged-data browser; the backend resolves every slice, so only a page is ever held.
export const StagedDataTable = <TRow, >(
    {
        requestId,
        failingOnly,
        search,
        mapper,
        noDataMessage
    }: IProps<TRow>) => {

    const [data, setData] = useState<SearchResult<TRow>>();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [pageNumber, setPageNumber] = useState<number>(1);
    const [searchValue, setSearchValue] = useState<string>("");
    const [entriesPerPage, setEntriesPerPage] = useState<EntriesPerPage>({defaultValue: 10});

    const pageSize = entriesPerPage.defaultValue ?? 10;

    useEffect(() => {
        const load = async () => {
            setIsLoading(true);
            try {
                const response = await search(requestId, {pageNumber, pageSize, searchValue, failingOnly});
                setData(response.data);
            } catch (error) {
                console.log(error);
            } finally {
                setIsLoading(false);
            }
        };

        load();
    }, [requestId, pageNumber, pageSize, searchValue, failingOnly]); // eslint-disable-line react-hooks/exhaustive-deps

    const entriesPerPageChanged = (entries: number) => {
        setEntriesPerPage({defaultValue: entries});
        setPageNumber(1);
    };

    const searchValueChanged = (value: string) => {
        setSearchValue(value);
        setPageNumber(1);
    };

    return (
        <DataTable
            table={mapper(data?.results ?? [])}
            entriesPerPage={entriesPerPage}
            pageNumber={pageNumber}
            totalItemsCount={data?.totalItemsCount ?? 0}
            filteredItemsCount={data?.filteredItemsCount ?? 0}
            isLoading={isLoading}
            canSearch
            showTotalEntries
            entriesPerPageChanged={entriesPerPageChanged}
            pageNumberChanged={setPageNumber}
            searchValueChanged={searchValueChanged}
            noDataMessage={noDataMessage}
        />
    );
};
